import React, { useState, useEffect } from 'react';
import { 
  FolderGit2, 
  Trash2, 
  Edit3, 
  Eye, 
  ExternalLink, 
  AlertCircle,
  CheckCircle,
  HelpCircle,
  Briefcase
} from 'lucide-react';
import { CustomProject } from '../types/productBuilder';

interface MyProjectsViewProps {
  onEditProject: (projectId: string, templateId?: string) => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const MyProjectsView: React.FC<MyProjectsViewProps> = ({
  onEditProject,
  onAddLog
}) => {
  const [projects, setProjects] = useState<CustomProject[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('nexorax_saved_projects');
    if (saved) {
      setProjects(JSON.parse(saved));
    }
  }, []);

  const deleteProject = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete the project "${name}"?`)) {
      const updated = projects.filter(p => p.id !== id);
      setProjects(updated);
      localStorage.setItem('nexorax_saved_projects', JSON.stringify(updated));
      onAddLog('My Projects', `Deleted project "${name}"`, 'SUCCESS', 'Removed target project instance.');
    }
  };

  const getStatusBadge = (status: CustomProject['status']) => {
    switch (status) {
      case 'Published':
        return <span className="px-2.5 py-0.5 rounded-full text-[9px] font-mono uppercase bg-emerald-950/40 text-emerald-400 border border-emerald-500/25">Live Staging</span>;
      case 'Needs Admin Review':
        return <span className="px-2.5 py-0.5 rounded-full text-[9px] font-mono uppercase bg-amber-950/40 text-amber-400 border border-amber-500/25 animate-pulse">Needs Review</span>;
      case 'In Progress':
        return <span className="px-2.5 py-0.5 rounded-full text-[9px] font-mono uppercase bg-cyan-950/40 text-cyan-400 border border-cyan-500/25">Managed Setup</span>;
      default:
        return <span className="px-2.5 py-0.5 rounded-full text-[9px] font-mono uppercase bg-zinc-900 text-zinc-400 border border-zinc-800">Local Draft</span>;
    }
  };

  return (
    <div className="space-y-6 text-left">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-sans font-black text-white uppercase tracking-tight">My Projects Workspace</h2>
          <p className="text-xs text-slate-400 mt-1">Review saved draft projects, check publication status, and request managed deployment setup.</p>
        </div>
        <div className="p-3 bg-[#00ffff]/5 border border-[#00ffff]/10 rounded-2xl flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-[#00ffff]">
          <FolderGit2 className="w-4 h-4" />
          <span>Active Nodes: {projects.length}</span>
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="p-12 text-center border border-white/5 rounded-3xl bg-slate-950/30 space-y-4">
          <FolderGit2 className="w-12 h-12 mx-auto text-zinc-600 stroke-[1.5]" />
          <div>
            <h3 className="text-sm font-semibold text-slate-300 font-mono uppercase">No saved projects found</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">Start customizing ready-made templates, upload a layout wireframe image, or follow guided selections inside Product Builder.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((proj) => (
            <div 
              key={proj.id}
              className="p-5 rounded-3xl bg-slate-950/80 border border-white/5 hover:border-slate-800 transition-all flex flex-col justify-between space-y-4 h-full"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  {getStatusBadge(proj.status)}
                  <span className="text-[9px] font-mono text-zinc-500">{new Date(proj.createdAt).toLocaleDateString()}</span>
                </div>
                <h3 className="text-sm font-bold text-white truncate">{proj.name}</h3>
                
                {/* Visual Specifications Summary list */}
                <div className="text-[10px] font-mono text-slate-400 space-y-1 bg-black/40 p-3 rounded-2xl border border-white/5">
                  <div className="flex justify-between"><span className="text-zinc-600 uppercase">Sector:</span> <span className="font-bold text-[#00ffff]">{proj.industry.toUpperCase()}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600 uppercase">Type:</span> <span className="truncate">{proj.projectType}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600 uppercase">Pages:</span> <span>{proj.pages.length} Pages</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600 uppercase">Theme:</span> <span className="text-purple-400">{proj.colors.swatchName}</span></div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => onEditProject(proj.id, proj.templateId)}
                  className="flex-1 py-2 bg-zinc-900 hover:bg-zinc-800 border border-white/5 text-slate-300 hover:text-white font-mono text-[10.5px] font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  <span>Customize</span>
                </button>
                <button
                  onClick={() => deleteProject(proj.id, proj.name)}
                  className="p-2 border border-red-500/20 hover:border-red-500 hover:bg-red-950/20 text-red-400 rounded-xl transition-all cursor-pointer"
                  title="Delete project"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
